import { FC } from 'react';
import { TeamMember } from '@demind-inc/core';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { TaskCheckBox } from '../../../common/TaskCheckBox/TaskCheckBox';
import TeamMemberAvatar from '../../TeamMemberAvatar';
import TeamTaskAssigneeSelect from '../../TeamTaskAssigneeSelect';
import {
  TeamTaskWithOwner,
  getTeamTaskAssigneeUserId,
  getTeamTaskDueLabel,
  getTeamTaskForDisplay,
  isTeamBoardTask,
} from '../../utils';
import { isAppleRemindersDesktopSupported } from '../../../../helpers';
import { BUCKET_CLASS_NAMES } from '../constants';
import { getOwnerShortName, getTaskProviderIcon } from '../helpers';
import type { TeamTasksSegment } from '../types';
import './TeamTaskRow.scss';

interface TeamTaskRowProps {
  taskItem: TeamTaskWithOwner;
  segment: TeamTasksSegment;
  members: TeamMember[];
  currentUserId: string;
  gridTemplateColumns: string;
  hideOwner: boolean;
  hideBoard: boolean;
  isTaskStatusUpdating: boolean;
  isAssigningTask: boolean;
  onOpenTask: (taskItem: TeamTaskWithOwner) => void;
  onToggleTask: (taskItem: TeamTaskWithOwner, completed: boolean) => void;
  onAssignTask: (taskItem: TeamTaskWithOwner, assigneeUserId: string) => void;
}

const TeamTaskRow: FC<TeamTaskRowProps> = ({
  taskItem,
  segment,
  members,
  currentUserId,
  gridTemplateColumns,
  hideOwner,
  hideBoard,
  isTaskStatusUpdating,
  isAssigningTask,
  onOpenTask,
  onToggleTask,
  onAssignTask,
}) => {
  const { t, i18n } = useTranslation();
  const task = getTeamTaskForDisplay(taskItem, t);
  const providerIcon = getTaskProviderIcon(task.appFrom);
  const isDone = taskItem.bucket === 'done' || !!task.completed;
  const isTeamTask = segment === 'team' && isTeamBoardTask(taskItem);
  const showCompletion = taskItem.permissions.canComplete;
  const isUnsupportedAppleReminder =
    task.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported();
  const isCompletionDisabled =
    !showCompletion ||
    isUnsupportedAppleReminder ||
    (task.recurring?.isRecurring === true && isDone);

  return (
    <div
      className={clsx(
        'team-tasks-view__row',
        showCompletion && 'team-tasks-view__row--own',
        isTeamTask && 'team-tasks-view__row--team-task'
      )}
      style={{ gridTemplateColumns }}
      onClick={() => onOpenTask(taskItem)}
    >
      <div className="team-tasks-view__cell team-tasks-view__cell--leading">
        {showCompletion && (
          <TaskCheckBox
            isCompleted={isDone}
            disabled={isCompletionDisabled || isTaskStatusUpdating}
            size={18}
            onChange={(checked) => onToggleTask(taskItem, checked)}
          />
        )}
      </div>

      <div className="team-tasks-view__cell team-tasks-view__cell--task">
        <Typography
          variant="body2"
          component="span"
          className={clsx(
            'team-tasks-view__task-title',
            isDone && 'team-tasks-view__task-title--done'
          )}
        >
          {task.name}
        </Typography>
      </div>

      {!hideOwner && (
        <div className="team-tasks-view__cell team-tasks-view__cell--owner">
          {isTeamTask ? (
            <TeamTaskAssigneeSelect
              members={members}
              currentUserId={currentUserId}
              value={getTeamTaskAssigneeUserId(taskItem)}
              disabled={isAssigningTask || !taskItem.permissions.canAssign}
              onChange={(assigneeUserId) => onAssignTask(taskItem, assigneeUserId)}
            />
          ) : (
            <>
              <TeamMemberAvatar
                name={taskItem.owner.name}
                initials={taskItem.owner.initials}
                photoUrl={taskItem.owner.photoUrl}
                size="medium"
              />
              <Typography
                variant="subtitle2"
                component="span"
                className="team-tasks-view__owner-name"
              >
                {getOwnerShortName(taskItem, t)}
              </Typography>
            </>
          )}
        </div>
      )}

      {!hideBoard && (
        <div className="team-tasks-view__cell team-tasks-view__cell--board">
          <span className="team-tasks-view__board-provider">
            {providerIcon && (
              <img
                src={providerIcon}
                alt=""
                aria-hidden="true"
                className="team-tasks-view__provider-icon"
              />
            )}
            <Typography
              variant="subtitle2"
              component="span"
              className="team-tasks-view__board-name"
            >
              {taskItem.project.name || t('team.tasks.noProject')}
            </Typography>
          </span>
        </div>
      )}

      <div className="team-tasks-view__cell team-tasks-view__cell--due">
        <span className="team-tasks-view__due-chip">
          <span
            className={clsx('team-tasks-view__bucket-dot', BUCKET_CLASS_NAMES[taskItem.bucket])}
            aria-hidden="true"
          />
          <Typography variant="subtitle2" component="span">
            {getTeamTaskDueLabel(task, taskItem.bucket, t, i18n.language)}
          </Typography>
        </span>
      </div>
    </div>
  );
};

export default TeamTaskRow;
